import { useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageList, move } from "./Images";

const LABELS = { courses: "CQB Courses", gear: "Merch", workshop: "Workshop / Repairs", killhouse: "Killhouse (Soon)", ethos: "Ethos", gallery: "Gallery", partners: "Partners", contact: "Contact" };

const Field = ({ label, children }) => (
  <label className="block"><span className="field-label">{label}</span>{children}</label>
);

const CustomEditor = ({ s, onChange }) => (
  <div className="space-y-5">
    <div className="grid sm:grid-cols-2 gap-5">
      <Field label="Title (English)"><input data-testid="custom-title-en" className="field" value={s.title_en} onChange={(e) => onChange({ ...s, title_en: e.target.value })} /></Field>
      <Field label="Title (Português)"><input data-testid="custom-title-pt" className="field" value={s.title_pt} onChange={(e) => onChange({ ...s, title_pt: e.target.value })} /></Field>
      <Field label="Text (English)"><textarea data-testid="custom-body-en" rows={5} className="field resize-none" value={s.body_en} onChange={(e) => onChange({ ...s, body_en: e.target.value })} /></Field>
      <Field label="Text (Português)"><textarea data-testid="custom-body-pt" rows={5} className="field resize-none" value={s.body_pt} onChange={(e) => onChange({ ...s, body_pt: e.target.value })} /></Field>
    </div>
    <div>
      <span className="field-label">Photos (more than one = slideshow, drag to reorder)</span>
      <div className="mt-3"><ImageList value={s.images || []} onChange={(images) => onChange({ ...s, images })} testid="custom-images" /></div>
    </div>
  </div>
);

export const SectionsPanel = ({ sections, onChange }) => {
  const [drag, setDrag] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const editing = sections.find((s) => s.key === editKey);
  const update = (key, next) => onChange(sections.map((s) => (s.key === key ? next : s)));
  const add = () => {
    const key = `c-${Date.now().toString(36)}`;
    onChange([...sections, { key, visible: true, custom: true, title_en: "New Section", title_pt: "Nova Secção", body_en: "", body_pt: "", images: [] }]);
    setEditKey(key);
  };

  return (
    <div>
      <p className="text-sm text-[var(--muted)] mb-5">Drag rows (or use the arrows) to reorder. The eye shows/hides a section. The opening section and footer always stay.</p>
      <ul data-testid="sections-list" className="border-t border-[var(--line)]">
        {sections.map((s, i) => (
          <li key={s.key} draggable data-testid={`section-row-${s.key}`}
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => { if (drag !== null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (drag !== null) onChange(move(sections, drag, i)); setDrag(null); }}
            onDragEnd={() => setDrag(null)}
            className={`flex items-center gap-3 border-b border-[var(--line)] py-3 px-2 ${drag === i ? "opacity-40" : ""} ${s.visible ? "" : "text-[var(--muted)]"}`}>
            <GripVertical className="w-4 h-4 text-[var(--muted)] cursor-grab shrink-0" />
            <span className="font-mono text-[11px] text-[var(--muted)] w-6">{String(i + 1).padStart(2, "0")}</span>
            <span className="flex-1 min-w-0 truncate font-display text-xl font-bold uppercase">
              {s.custom ? s.title_en || "Untitled" : LABELS[s.key]}
              {s.custom && <span className="ml-3 font-mono text-[10px] tracking-[0.2em] border border-[var(--line)] px-1.5 py-0.5 align-middle">CUSTOM</span>}
            </span>
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => onChange(move(sections, i, i - 1))} className="p-1.5 hover:text-white disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
            <button type="button" aria-label="Move down" disabled={i === sections.length - 1} onClick={() => onChange(move(sections, i, i + 1))} className="p-1.5 hover:text-white disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
            <button type="button" aria-label={s.visible ? "Hide" : "Show"} data-testid={`section-toggle-${s.key}`} onClick={() => update(s.key, { ...s, visible: !s.visible })} className="p-1.5 hover:text-white">
              {s.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            {s.custom && (
              <>
                <button type="button" aria-label="Edit" data-testid={`section-edit-${s.key}`} onClick={() => setEditKey(s.key)} className="p-1.5 hover:text-white"><Pencil className="w-4 h-4" /></button>
                <button type="button" aria-label="Delete" data-testid={`section-delete-${s.key}`} onClick={() => window.confirm("Delete this section?") && onChange(sections.filter((x) => x.key !== s.key))} className="p-1.5 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </li>
        ))}
      </ul>
      <button type="button" data-testid="section-add-button" onClick={add}
        className="mt-6 inline-flex items-center gap-2 clip-cut-sm border border-[var(--line)] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:border-white transition-colors">
        <Plus className="w-4 h-4" /> Add custom section
      </button>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditKey(null)}>
        <DialogContent data-testid="custom-section-dialog" className="bg-[var(--ink-2)] border-[var(--line)] rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">Edit section</DialogTitle></DialogHeader>
          {editing && <CustomEditor s={editing} onChange={(next) => update(editing.key, next)} />}
          <button type="button" data-testid="custom-section-done" onClick={() => setEditKey(null)}
            className="w-full clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[var(--bone)]">Done</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
